import Link from "next/link";
import { useSearchParams } from "next/navigation";

interface PrivacyPolicyPageProps {
    searchParams?: any
}

function getSafeReturnTo(returnTo: string | string[] | undefined) {
    const value = Array.isArray(returnTo) ? returnTo[0] : returnTo
    if (!value || !value.startsWith("/") || value.startsWith("//")) return "/"
    return value
}

export default function PrivacyPolicyPage() {
    const searchParams = useSearchParams()
    const returnToRaw = searchParams.get('returnTo') || undefined
    const returnTo = getSafeReturnTo(returnToRaw)

    return (
        <main className="min-h-screen bg-gray-50" role="main" aria-label="Privacy Policy">
            <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
                <Link
                    href={returnTo}
                    className="mb-8 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                    aria-label="Return to ReliefHub"
                >
                    ← Back to ReliefHub
                </Link>

                <article
                    className="max-w-none text-gray-700"
                    role="document"
                    aria-label="ReliefHub Privacy Policy"
                    lang="en"
                >
                    <h1 className="text-3xl font-bold text-gray-900 mb-2" id="privacy-policy-title">
                        Privacy Policy
                    </h1>
                    <p className="text-sm text-gray-500 mb-2" aria-live="polite">
                        Last updated:{" "}
                        <time dateTime="2025-03-08">March 8, 2025</time>
                    </p>

                    <p className="text-sm font-medium text-gray-700 bg-gray-100 p-3 rounded-md mb-8">
                        By creating an account or using ReliefHub, you agree to this Privacy Policy.
                    </p>

                    <h2 id="who-we-are" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Who we are
                    </h2>
                    <p className="mb-4">
                        ReliefHub is a disaster relief management platform operated by{" "}
                        <a
                            href="https://adventures.org"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                            aria-label="Adventures in Missions (opens in new tab)"
                        >
                            Adventures in Missions
                        </a>
                        . Our main website is at{" "}
                        <a
                            href="https://adventures.org"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                            aria-label="adventures.org (opens in new tab)"
                        >
                            https://adventures.org
                        </a>
                        .
                    </p>

                    <h2 id="information-we-collect" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Information we collect
                    </h2>
                    <p className="mb-4">
                        When you register and use ReliefHub, we collect the information you
                        provide in your profile, including your name, email address, phone
                        number, organization, skills and experience, availability, and other
                        details relevant to disaster relief volunteer coordination. We also
                        collect data you enter about homes, homeowners, work orders, and
                        related project information.
                    </p>

                    <h2 id="comments" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Comments
                    </h2>
                    <p className="mb-4">
                        When users add comments to work orders or other features within the
                        platform, we collect the comment content, the user who created it,
                        and timestamps. This data is used for coordinating relief work and
                        is retained as part of the work order history.
                    </p>

                    <h2 id="photos-exif" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Photos and media – EXIF data
                    </h2>
                    <p className="mb-4">
                        <strong>Unlike many websites that advise removing location data from
                        photos, ReliefHub intentionally collects and uses EXIF (Exchangeable
                        Image File) metadata from photos you upload.</strong> This includes:
                    </p>
                    <ul className="my-4 ml-6 list-disc space-y-2">
                        <li>
                            <strong>Date and time</strong> – When the photo was taken (used
                            for photo timelines and documentation)
                        </li>
                        <li>
                            <strong>GPS coordinates</strong> – Location where the photo
                            was captured (used for mapping damage, verifying work sites,
                            and organizing recovery efforts)
                        </li>
                        <li>
                            <strong>Camera details</strong> – Device make and model (used
                            for technical support and quality assessment)
                        </li>
                    </ul>
                    <p className="mb-4">
                        We use this data to support disaster relief operations—for example,
                        to document damage locations, verify work completion, and maintain
                        accurate photo timelines per home. If you prefer not to share
                        location data, you may strip EXIF data from your photos before
                        uploading using third-party tools; however, we will not be able to
                        automatically record capture date or GPS coordinates for those
                        images.
                    </p>

                    <h2 id="cookies" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Cookies
                    </h2>
                    <p className="mb-4">
                        ReliefHub uses cookies for the following purposes:
                    </p>
                    <ul className="my-4 ml-6 list-disc space-y-2">
                        <li>
                            <strong>Authentication</strong> – When you log in, we set an
                            HTTP-only cookie to keep you signed in.                             This cookie:
                            <ul className="my-2 ml-4 list-disc space-y-1">
                                <li>
                                    Stores an encrypted session token (no password or
                                    personal data is stored in the cookie)
                                </li>
                                <li>Lasts up to 7 days</li>
                                <li>
                                    Is removed when you log out or when the session expires
                                </li>
                            </ul>
                        </li>
                        <li>
                            <strong>Security</strong> – The cookie is configured with
                            secure settings (HTTP-only, SameSite, and Secure flag in
                            production) to protect your session.
                        </li>
                    </ul>
                    <p className="mb-4">
                        We do not use cookies for advertising, tracking, or third-party
                        analytics. We do not set cookies for saving comment form details
                        or screen display preferences like the main Adventures.org website.
                    </p>

                    <h2 id="third-party" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Third-party services
                    </h2>
                    <p className="mb-4">
                        ReliefHub uses the following third-party services. Each has its
                        own privacy policy:
                    </p>
                    <ul className="my-4 ml-6 list-disc space-y-2">
                        <li>
                            <strong>Google</strong> – For optional sign-in with Google
                            accounts. See{" "}
                            <a
                                href="https://policies.google.com/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                                aria-label="Google Privacy Policy (opens in new tab)"
                            >
                                Google Privacy Policy
                            </a>
                            .
                        </li>
                        <li>
                            <strong>Amazon Web Services (AWS)</strong> – For secure
                            storage of photos and media. See{" "}
                            <a
                                href="https://aws.amazon.com/privacy/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                                aria-label="AWS Privacy (opens in new tab)"
                            >
                                AWS Privacy
                            </a>
                            .
                        </li>
                    </ul>

                    <h2 id="text-messages" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Text messages
                    </h2>
                    <p className="mb-4">
                        We may send you text messages for app notifications (for example, when
                        someone tags you in a project or group note) and for account security
                        (e.g., two-factor authentication). You can opt in or out of notification
                        texts in your Profile settings. Message and data rates may apply.
                        Consent to receive these messages is not required to use our services.
                    </p>

                    <h2 id="embedded-content" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Embedded content
                    </h2>
                    <p className="mb-4">
                        Pages within ReliefHub may include embedded content (e.g., maps,
                        images, links). Embedded content from other websites behaves as if
                        you had visited that site directly. Those sites may collect data,
                        use cookies, and track your interaction with their content.
                    </p>

                    <h2 id="data-sharing" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Who we share your data with
                    </h2>
                    <p className="mb-4">
                        We do not sell your personal data. We share data only as needed
                        to operate ReliefHub (e.g., with service providers like AWS) or as
                        required by law. If you request a password reset, your IP address
                        may be included in the reset email.
                    </p>

                    <h2 id="retention" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        How long we retain your data
                    </h2>
                    <p className="mb-4">
                        We retain your profile information and the data you create (homes,
                        work orders, photos, comments) for as long as your account is
                        active and as needed for disaster relief operations and legal
                        compliance. You may request deletion of your personal data subject
                        to our obligations to retain certain records for administrative,
                        legal, or security purposes.
                    </p>

                    <h2 id="your-rights" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Your rights over your data
                    </h2>
                    <p className="mb-4">
                        If you have an account on ReliefHub, you can request an export of
                        the personal data we hold about you. You may also request that we
                        erase your personal data. This does not include data we are
                        required to retain for administrative, legal, or security purposes.
                        Contact us using the information below to exercise these rights.
                    </p>

                    <h2 id="data-location" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Where your data is sent
                    </h2>
                    <p className="mb-4">
                        Your data is stored and processed on servers in the United States.
                        Photo and media files are stored in secure cloud storage (AWS).
                    </p>

                    <h2 id="security" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Security
                    </h2>
                    <p className="mb-4">
                        We take the security of your data seriously. ReliefHub uses HTTPS
                        (encryption in transit) for all connections. Passwords are hashed
                        and never stored in plain text. Session tokens are stored in
                        HTTP-only cookies to reduce exposure to scripting attacks. Access
                        to personal data is restricted by role-based permissions, and we
                        work with trusted service providers who maintain industry-standard
                        security practices.
                    </p>

                    <h2 id="policy-changes" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Changes to this policy
                    </h2>
                    <p className="mb-4">
                        We may update this Privacy Policy from time to time. We will
                        notify you of material changes through our in-app notification
                        system. For significant updates that affect how we use your
                        personal data, we may also send an email to your registered
                        address. The &quot;Last updated&quot; date at the top of this page
                        indicates when the policy was last revised. Continued use of
                        ReliefHub after changes take effect constitutes your acceptance
                        of the updated policy. We encourage you to review this page
                        periodically.
                    </p>

                    <h2 id="data-breach" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Data breach notification
                    </h2>
                    <p className="mb-4">
                        In the unlikely event of a data breach that affects your personal
                        information, we will notify you as required by applicable law.
                        Notification may be sent by email to your registered address and/or
                        through our in-app notification system. We will describe the nature
                        of the breach, what information may have been affected, and what
                        steps we are taking in response. We will also provide guidance on
                        any steps you can take to protect yourself.
                    </p>

                    <h2 id="childrens-privacy" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Children&apos;s privacy
                    </h2>
                    <p className="mb-4">
                        ReliefHub may be used by volunteers under the age of 13 (for
                        example, when participating with family or youth groups). If you are
                        under 13, we require that a parent or guardian review this privacy
                        policy and provide consent before you create an account or provide
                        any personal information.
                    </p>
                    <p className="mb-4">
                        We do not knowingly collect personal information from children
                        under 13 without verifiable parental consent. If we learn that we
                        have collected personal information from a child under 13 without
                        proper consent, we will delete that information as quickly as
                        possible. Parents may contact us to review, correct, or delete any
                        personal information we have collected from their child.
                    </p>

                    <h2 id="contact" className="mt-10 mb-4 text-xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                        Contact
                    </h2>
                    <p className="mb-4">
                        For privacy-related questions or to exercise your rights, contact
                        Adventures in Missions:
                    </p>
                    <ul className="my-4 ml-6 list-disc space-y-2">
                        <li>
                            <strong>Toll Free:</strong>{" "}
                            <a
                                href="tel:8008812461"
                                className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                                aria-label="Call toll free 800-881-2461"
                            >
                                800-881-2461
                            </a>
                        </li>
                        <li>
                            <strong>Local:</strong>{" "}
                            <a
                                href="tel:7709831060"
                                className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                                aria-label="Call local 770-983-1060"
                            >
                                770-983-1060
                            </a>
                        </li>
                        <li>
                            <strong>Web:</strong>{" "}
                            <a
                                href="https://adventures.org/contact/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                                aria-label="Adventures in Missions contact page (opens in new tab)"
                            >
                                adventures.org/contact
                            </a>
                        </li>
                    </ul>

                    <hr className="my-8" />

                    <p className="text-sm text-gray-600 mb-4">
                        This privacy policy is adapted from the{" "}
                        <a
                            href="https://adventures.org/about/privacy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-700 underline hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 rounded"
                            aria-label="Adventures in Missions Privacy Policy (opens in new tab)"
                        >
                            Adventures in Missions Privacy Policy
                        </a>{" "}
                        and has been modified to reflect ReliefHub&apos;s specific features,
                        including intentional EXIF data collection for disaster relief
                        operations.
                    </p>
                </article>
            </div>
        </main>
    );
}
